import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import merchantService, { 
  Merchant, 
  CreateMerchantDto, 
  UpdateMerchantDto, 
  MerchantFilters,
  PaginatedResponse 
} from '@/services/merchantService'

interface MerchantState {
  merchants: Merchant[]
  currentMerchant: Merchant | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: MerchantFilters

  // Actions
  setFilters: (filters: MerchantFilters) => void
  fetchMerchants: (filters?: MerchantFilters) => Promise<void>
  fetchMerchantById: (id: string) => Promise<void>
  createMerchant: (data: CreateMerchantDto) => Promise<Merchant>
  updateMerchant: (id: string, data: UpdateMerchantDto) => Promise<Merchant>
  deleteMerchant: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentMerchant: () => void
}

const useMerchantStore = create<MerchantState>()(
  devtools(
    (set, get) => ({
      merchants: [],
      currentMerchant: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchMerchants: async (filters = {}) => {
        try {
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          const response: PaginatedResponse<Merchant> = await merchantService.getAll(currentFilters)
          
          set({
            merchants: response.merchants,
            pagination: response.pagination,
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement des commerçants',
            loading: false
          })
        }
      },

      fetchMerchantById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const merchant = await merchantService.getById(id)
          set({ currentMerchant: merchant, loading: false })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement du commerçant',
            loading: false
          })
        }
      },

      createMerchant: async (data: CreateMerchantDto) => {
        try {
          set({ loading: true, error: null })
          const merchant = await merchantService.create(data)
          
          // Add to merchants list
          set((state) => ({
            merchants: [merchant, ...state.merchants],
            loading: false
          }))

          return merchant
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la création du commerçant',
            loading: false
          })
          throw error
        }
      },

      updateMerchant: async (id: string, data: UpdateMerchantDto) => {
        try {
          set({ loading: true, error: null })
          const merchant = await merchantService.update(id, data)
          
          // Update in merchants list
          set((state) => ({
            merchants: state.merchants.map(m => 
              m.merchant_id === id ? merchant : m
            ),
            currentMerchant: state.currentMerchant?.merchant_id === id ? merchant : state.currentMerchant,
            loading: false
          }))

          return merchant
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la mise à jour du commerçant',
            loading: false
          })
          throw error
        }
      },

      deleteMerchant: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await merchantService.delete(id)
          
          // Remove from merchants list
          set((state) => ({
            merchants: state.merchants.filter(m => m.merchant_id !== id),
            currentMerchant: state.currentMerchant?.merchant_id === id ? null : state.currentMerchant,
            loading: false
          }))
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la suppression du commerçant',
            loading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentMerchant: () => set({ currentMerchant: null })
    }),
    { name: 'MerchantStore' }
  )
)

export default useMerchantStore
